import styles from "./components.module.scss";
import React, { FC, useMemo, useRef, useState } from "react";
import ShiftDescriptionContainer from "./ShiftDescriptionContainer";
import { exitReact } from "../../../../utils";
import { JOB_STATUSES } from "../../../dentist/PostJobPage/PostJob.utils";
import { usePersistedState } from "../../../../hooks";
import { HygienistProps, JobProps } from "utils/types";
import { entities } from "../../../../reduxStore";
import FixedLoader from "../../../../components/FixedLoader/FixedLoader";
import FixedError from "../../../../components/FixedError/FixedError";
import ApiError from "../../../../api/ApiError";
import useActionApi from "../../../../hooks/useActionApi";
import Api from "../../../../api";
import dayjs from "dayjs";
import Popup from "../../../../components/Popup";
import { MembershipRestrictionModal, type MembershipDialogCopy } from "../../../../components/Modal";
import { PARTIAL_OFFER_STATUS } from "utils/constants";
import { ContainedButton, OutlinedButton, Description } from "../AvailableJob.styles";
import MaxBookedPopup from "./MaxBookedPopup";
import { Analytics } from "utils/analytics";
import LanguageSelectionModal from "../../../../components/LanguageSelectionModal";
import { useLanguageCheck } from "../../../../hooks/useLanguageCheck";
import { getMembershipRestrictionDialogCopy } from "../../../../utils/membership";
import instabook from 'images/job-option/instabook.png';

// @ts-ignore
const TempStars = window.TempStars

interface MakeAnInstabookOfferProps {
    job: JobProps,
    onClose: () => void
    onSubmit: (rate: number) => void
    submitting: boolean
}

const MakeAnInstabookOfferPopup: FC<MakeAnInstabookOfferProps> = ({ job, onClose, onSubmit, submitting }) => {
    const from = job.startDate?.replace(/-/g, "/") + ' ' + (job.shifts?.[0]?.postedStart ?? '');
    const to = job.startDate?.replace(/-/g, "/") + ' ' + (job.shifts?.[0]?.postedEnd ?? '');

    return (
        <div className={styles.popupContainer}>
            <div className={styles.titleContainer}>
                <h2 className={styles.popupTitle}>Instantly Book This Shift?</h2>
                <p className={styles.popupSubtitle}>
                    Important! Read Carefully
                </p>
            </div>
            <div className={styles.contentContainer}>
                <div className={styles.modalDescription}>
                    <span className={styles.emphasizedText}>You're about to book this shift.</span>
                </div>
                <div className={styles.modalDescription}>
                    <span className={styles.emphasizedText}>This is not like sending an offer</span> - you will be instantly booked and confirmed for this shift. The office and their patients will be counting on you to keep your commitment.
                </div>
                <div className={styles.modalDescription}>
                    <span className={styles.emphasizedText}>Review the details carefully</span> and click "Book This Shift Now" if you agree to commit to this shift, otherwise click "Cancel".
                </div>
                <div className={styles.shiftDescriptionWrapper}>
                    <ShiftDescriptionContainer
                        header={job.location.practiceName}
                        address={job.location.address}
                        from={from}
                        to={to}
                        rate={job.proposedRate}
                        isConfirmModal={true}
                    />
                </div>
            </div>

            <ContainedButton
                data-testid="available-job-instabook-confirm"
                disabled={submitting}
                className={styles.modalButton}
                variant="contained"
                size="large"
                onClick={() => {
                    onSubmit(job.proposedRate)
                    onClose()
                }}          >
                Book This Shift Now
            </ContainedButton>
            <OutlinedButton
                data-testid="available-job-instabook-cancel"
                className={styles.modalButton}
                variant="outlined"
                size="large"
                onClick={onClose}
            >
                Cancel
            </OutlinedButton>
        </div>
    );
}


const InstaBookOfferModal: FC<{ job: JobProps, history: any, trackData: any, isFavourite: boolean, isMaxBooked?: boolean, membershipRateTier?: number, membershipStatusCode?: string | null, canUseInstabook?: boolean, canRequestMaxBookedIncrease?: boolean, onEligibilityChanged?: () => void }> = (
    {
        job,
        history,
        trackData,
        isFavourite,
        isMaxBooked,
        membershipRateTier,
        membershipStatusCode,
        canUseInstabook,
        canRequestMaxBookedIncrease = false,
        onEligibilityChanged,
    }) => {
    const [isOpenMakeInstabookOfferModal, setIsOpenMakeInstabookOfferModal] = useState<boolean>(false);
    const [isOpenConfirmInstabookOfferModal, setIsOpenConfirmInstabookOfferModal] = useState<boolean>(false);
    const hygienist = usePersistedState<HygienistProps>({
        entityName: entities.hygienist
    })[0];
    const [isMaxBookedPopupOpen, setIsMaxBookedPopupOpen] = useState<boolean>(false);
    
    // Language modal state
    const [showLanguageModal, setShowLanguageModal] = useState<boolean>(false);
    const [pendingConfirmation, setPendingConfirmation] = useState<boolean>(false);
    const [membershipDialog, setMembershipDialog] = useState<MembershipDialogCopy | null>(null);
    const [bookingError, setBookingError] = useState<ApiError | null>(null);
    const submittingRef = useRef(false);
    const [submitting, setSubmitting] = useState(false);
    const { checkIfLanguagesNeeded } = useLanguageCheck();
    const finishSubmitting = () => {
        submittingRef.current = false;
        setSubmitting(false);
    };

    const [makeInstabookOfferLoading, , makeInstabookOffer] = useActionApi({
        apiMethod: Api.makeInstabookOffer,
        onSuccess: ({ offerId }: { offerId: number }) => {
            if (offerId) confirmInstabook({ offerId, jobId: job.id });
            else {
                finishSubmitting();
                setBookingError(new ApiError(0, 'We could not confirm this booking. Please try again.'));
            }
            setIsOpenMakeInstabookOfferModal(false)
        },
        onError: (e) => {
            finishSubmitting();
            const membershipRestrictionDialog = getMembershipRestrictionDialogCopy(e);
            if (membershipRestrictionDialog) {
                setMembershipDialog(membershipRestrictionDialog);
            } else {
                setBookingError(e instanceof ApiError ? e : new ApiError(0, 'We could not confirm this booking. Please try again.'));
            }
            setIsOpenMakeInstabookOfferModal(false)
            onEligibilityChanged?.();
        }
    });

    const [confirmInstabookLoading, , confirmInstabook] = useActionApi({
        apiMethod: Api.confirmInstabook,
        onSuccess: async () => {
            Analytics.track('instabook shift', trackData);
            
            // NEW: Check if user needs to add languages before showing confirmation popup
            try {
                const needsLanguages = await checkIfLanguagesNeeded();
                if (needsLanguages) {
                    setShowLanguageModal(true);
                    setPendingConfirmation(true);
                    finishSubmitting();
                    return; // Don't show confirmation popup yet
                }
            } catch (error) {
                // Language check failed, continue with normal flow
                console.error('Language check failed, continuing with normal flow:', error);
            }
            
            // Original flow - show confirmation popup
            setIsOpenConfirmInstabookOfferModal(true);
            finishSubmitting();
        },
        onError: (e) => {
            finishSubmitting();
            setIsOpenConfirmInstabookOfferModal(false)
            const membershipRestrictionDialog = getMembershipRestrictionDialogCopy(e);
            if (membershipRestrictionDialog) {
                setMembershipDialog(membershipRestrictionDialog);
            } else {
                setBookingError(e instanceof ApiError ? e : new ApiError(0, 'We could not confirm this booking. Please try again.'));
            }
        }
    });

    const [revertInstabookLoading, , revertInstabook] = useActionApi({
        apiMethod: Api.revertInstabook,
        onSuccess: () => {
            setIsOpenConfirmInstabookOfferModal(false)
            Analytics.track('instabook shift', trackData);
            history.push('loading')
            ;(window as any).TempStars?.App?.goReact('hygienistHome')
        },
    });

    const onGoHome = () => {
        setIsOpenConfirmInstabookOfferModal(false)
        history.push('loading')
        ;(window as any).TempStars?.App?.goReact('hygienistHome')
    }

    // Language modal handlers
    const handleLanguageSubmissionSuccess = () => {
        setShowLanguageModal(false);
        if (pendingConfirmation) {
            // Show the confirmation popup now that languages are submitted
            setIsOpenConfirmInstabookOfferModal(true);
            setPendingConfirmation(false);
        }
    };

    const handleLanguageModalClose = () => {
        // Allow closing the modal, but still show confirmation
        setShowLanguageModal(false);
        if (pendingConfirmation) {
            // Continue with confirmation popup even if user closes language modal
            setIsOpenConfirmInstabookOfferModal(true);
            setPendingConfirmation(false);
        }
    };

    const oldOfferId = useMemo(() => {
        return job?.partialOffers?.find((offer: any) => offer.hygienistId === hygienist.id
            && offer.status === PARTIAL_OFFER_STATUS.REMOVED)?.id
    }, [job])
    
    // The parent resolves this from the full canonical membership response.
    // Missing capability data is fail-closed; rate tier and raw status are not
    // substitutes for the booking authority contract.
    const membershipInstabookEligible = canUseInstabook === true;
    const isShowInstabookBtn = (job.status === JOB_STATUSES.POSTED || job.status === JOB_STATUSES.PARTIAL || (JOB_STATUSES.OFFER_EXPIRED === job.status && oldOfferId))
        && Number(job.Instabook) === 1 && membershipInstabookEligible

    const isAvailableBooking = () => {
        if (isMaxBooked !== undefined) return !isMaxBooked;
        return (TempStars.Hygienist.getBookedShiftsNumber() < hygienist.maxBooked);
    };

    const onMakeInstabookOffer = async (rate: number) => {
        if (submittingRef.current) return;
        setBookingError(null);
        if (!isAvailableBooking()) {
            setIsMaxBookedPopupOpen(true);
            return;
        }
        submittingRef.current = true;
        setSubmitting(true);

        const offeredStartTime = dayjs(job.startDate + ' ' + (job.shifts?.[0]?.postedStart ?? ''), 'YYYY-MM-DD h:mm a').format('YYYY-MM-DD HH:mm')
        const offeredEndTime = dayjs(job.startDate + ' ' + (job.shifts?.[0]?.postedEnd ?? ''), 'YYYY-MM-DD h:mm a').format('YYYY-MM-DD HH:mm')

        Analytics.track('Instabook_Confirmed', {
            job_id: job.id,
            location_id: job.locationId,
            proposed_rate: job.proposedRate,
            confirmed_rate: rate,
            shift_date: job.startDate,
            shift_start: job.shifts?.[0]?.postedStart ?? null,
            shift_end: job.shifts?.[0]?.postedEnd ?? null,
            is_favourite: !!isFavourite,
            talent_status: hygienist?.status,
            membership_status_code: membershipStatusCode ?? null,
            membership_rate_tier: membershipRateTier ?? null,
        });

        await makeInstabookOffer({
            hourlyRate: rate,
            offeredStartTime,
            offeredEndTime,
            jobId: job.id,
            startDate: job.startDate,
            available: true,
        });
        // Do NOT navigate here. useApi/useActionApi RESOLVES even on error (the
        // onError handler shows the MembershipRestrictionModal for an FFS/
        // membership rejection), so navigating on resolve would immediately bounce
        // the Talent home and hide that modal. The success path navigates from the
        // confirmation popup's "Go Home" (onGoHome) after confirmInstabook succeeds.
    }

    const InstabookShiftPopup = () => {
        const from = job.startDate?.replace(/-/g, "/") + ' ' + (job.shifts?.[0]?.postedStart ?? '');
        const to = job.startDate?.replace(/-/g, "/") + ' ' + (job.shifts?.[0]?.postedEnd ?? '');
        return (
            <div className={styles.popupContainer}>
                <div className={styles.titleContainer}>
                    <h2 className={styles.popupTitle__confirm}>Nicely Done! You're all set.</h2>
                    <p className={styles.popupSubtitle}>
                        Important! Read Carefully
                    </p>
                </div>
                <div className={styles.contentContainer}>
                    <div className={styles.modalDescription}>
                        <span className={styles.emphasizedText}>You're booked and confirmed for your shift. {job.location.practiceName} and their patients are now counting on you.</span>
                    </div>
                    <div className={styles.modalDescription}>
                        We sent you a confirmation email with all the details, make sure you received it and review carefully.
                    </div>
                    <div className={styles.shiftDescriptionWrapper}>
                        <ShiftDescriptionContainer
                            header={job.location.practiceName}
                            address={job.location.address}
                            from={from}
                            to={to}
                            rate={job.ghr || job.proposedRate}
                            isConfirmModal={true}
                        />
                    </div>
                </div>

                <ContainedButton
                    className={styles.modalButton}
                    variant="contained"
                    size="large"
                    onClick={onGoHome}
                >
                    Ok
                </ContainedButton>
            </div>
        );
    }

    const instabookHandler = () => {
        if (submittingRef.current) return;
        Analytics.track('Instabook_Button_Clicked', {
            job_id: job.id,
            location_id: job.locationId,
            proposed_rate: job.proposedRate,
            shift_date: job.startDate,
            shift_start: job.shifts?.[0]?.postedStart ?? null,
            shift_end: job.shifts?.[0]?.postedEnd ?? null,
            is_favourite: !!isFavourite,
            talent_status: hygienist?.status,
            membership_status_code: membershipStatusCode ?? null,
            membership_rate_tier: membershipRateTier ?? null,
        });
        if (isAvailableBooking()) {
            setIsOpenMakeInstabookOfferModal(true);
        } else {
            setIsMaxBookedPopupOpen(true);
        }
    }

    return (<>
        <Popup opened={isOpenMakeInstabookOfferModal} onClose={() => setIsOpenMakeInstabookOfferModal(false)}>
            <MakeAnInstabookOfferPopup
                job={job}
                onClose={() => setIsOpenMakeInstabookOfferModal(false)}
                onSubmit={onMakeInstabookOffer}
                submitting={submitting}
            />
        </Popup>

        <Popup opened={isOpenConfirmInstabookOfferModal} onClose={() => setIsOpenConfirmInstabookOfferModal(false)}>
            <InstabookShiftPopup />
        </Popup>
        <MembershipRestrictionModal
            isOpen={!!membershipDialog}
            copy={membershipDialog}
            onClose={() => setMembershipDialog(null)}
        />
        {bookingError && <FixedError error={bookingError} onDismiss={() => setBookingError(null)} />}
        <MaxBookedPopup
            isOpen={isMaxBookedPopupOpen}
            onClose={() => setIsMaxBookedPopupOpen(false)}
            canRequestIncrease={canRequestMaxBookedIncrease}
        />

        {/* The top-of-page banner already communicates Instabook benefits. Keep this area concise. */}

        {isShowInstabookBtn && !job.Autobook && <ContainedButton
            data-testid="available-job-instabook-start"
            disabled={submitting || showLanguageModal || isOpenConfirmInstabookOfferModal}
            variant="contained"
            size="large"
            onClick={instabookHandler}
            startIcon={<img src={instabook} alt="" style={{ height: '1.25em', width: 'auto' }} />}
        >
            Instantly Book This Shift @ ${job.proposedRate}/hr
        </ContainedButton>}

        {/* Language Selection Modal */}
        <LanguageSelectionModal
            open={showLanguageModal}
            onClose={handleLanguageModalClose}
            onSuccess={handleLanguageSubmissionSuccess}
        />

        <FixedLoader
            loading={makeInstabookOfferLoading || confirmInstabookLoading || revertInstabookLoading}
        />
    </>)
}

export default InstaBookOfferModal;
